/* eslint-disable no-console, no-magic-numbers, max-statements, no-undef, camelcase, complexity, max-depth, consistent-return */
const RANDOM_BYTES_LENGTH = 64;
const MILLISECONDS_TO_SECONDS = 1000;
const NEAR_EXPIRATION_THRESHOLD = 60;
const DEFAULT_OTP_LENGTH = 4;

class BifrostApiError extends Error {
    constructor(errorData, statusCode) {
        super(errorData.error || "API Error");
        this.name = "BifrostApiError";
        this.errorCode = errorData.error_code;
        this.statusCode = statusCode;
        this.errorData = errorData;
    }
}

export class Bifrost {
    constructor({ authApiBaseUrl, clientId, deviceId, redirectUri, onSuccess, appLoginEvent }) {
        this.authApiBaseUrl = authApiBaseUrl;
        this.clientId = clientId;
        this.deviceId = deviceId;
        this.redirectUri = redirectUri;
        this.codeVerifier = "";
        this.cfWorkerBaseUrl = "";
        this.otpLength = DEFAULT_OTP_LENGTH;
        try {
            const url = new URL(redirectUri);
            this.cfWorkerBaseUrl = `${url.protocol}//${url.host}`;
        } catch {
            this.cfWorkerBaseUrl = "";
        }
        this.onSuccess = onSuccess;
        this.appLoginEvent = appLoginEvent;
    }

    getApiBaseUrl() {
        return this.authApiBaseUrl;
    }

    getClientId() {
        return this.clientId;
    }

    getDeviceId() {
        return this.deviceId;
    }

    getRedirectUri() {
        return this.redirectUri;
    }

    getOtpLength() {
        return this.otpLength;
    }

    get config() {
        return {
            authApiBaseUrl: this.authApiBaseUrl,
            clientId: this.clientId,
            deviceId: this.deviceId,
            redirectUri: this.redirectUri
        };
    }

    setAppLoginEvent(appLoginEvent) {
        this.appLoginEvent = appLoginEvent;
    }

    /**
     * Checks if the current environment is a WebView (Android or iOS)
     * @returns {boolean} True if running in a WebView
     */
    isWebView() {
        if (typeof window === "undefined") return false;

        const userAgent =
            window.navigator.userAgent || window.navigator.vendor || window.opera;

        // Check for common WebView identifiers
        return (
            /WebView|wv/.test(userAgent) || // Android WebView (including 'wv' identifier)
            (/\(iPhone|iPad|iPod;/.test(userAgent) && /AppleWebKit(?!.*Safari)/.test(userAgent)) // iOS WebView
        );
    }

    /**
     * Exchanges token for auth code and posts message to React Native WebView
     * @param {string} token - Access token to exchange
     * @returns {Promise<void>} Resolves when exchange is complete
     */
    async handleWebViewTokenExchange(token) {
        try {
            const exchangeData = await this.exchangeTokenForCode({ token });

            if (exchangeData?.code && window.ReactNativeWebView) {
                window.ReactNativeWebView.postMessage(
                    JSON.stringify({
                        eventAction: "UMS_APP_LOGIN_EVENT",
                        eventData: {
                            umsAuthCode: exchangeData.code
                        }
                    })
                );
            }
        } catch (error) {
            console.error("WebView token exchange failed:", error);
        }
    }

    async getLoginMethods() {
        try {
            const response = await fetch(`${this.authApiBaseUrl}/oauth2/method/${this.clientId}`, {
                method: "GET",
                headers: { Accept: "application/json" }
            });

            if (!response.ok) {
                const apiError = await this.handleApiError(response);
                throw apiError;
            }

            return await response.json();
        } catch (error) {
            console.error("Error fetching login methods::", error);

            if (error instanceof BifrostApiError) {
                throw error;
            }

            throw new Error(
                `Failed to fetch login methods: ${error instanceof Error ? error.message : "Unknown error"}`
            );
        }
    }

    async generatePKCES256() {
        const array = new Uint8Array(RANDOM_BYTES_LENGTH);
        crypto.getRandomValues(array);

        const codeVerifier = btoa(String.fromCharCode.apply(null, Array.from(array)))
            .replace(/=/g, "")
            .replace(/\+/g, "-")
            .replace(/\//g, "_");

        const buffer = await crypto.subtle.digest("SHA-256", new TextEncoder().encode(codeVerifier));
        const codeChallenge = btoa(String.fromCharCode.apply(null, Array.from(new Uint8Array(buffer))))
            .replace(/=/g, "")
            .replace(/\+/g, "-")
            .replace(/\//g, "_");

        this.codeVerifier = codeVerifier;
        return { codeChallenge, codeVerifier };
    }

    async startLoginFlow(params) {
        const { codeChallengeMethod = "S256", codeChallenge, redirectUri, state } = params;

        const urlParams = new URLSearchParams({
            response_type: "code",
            client_id: this.clientId,
            redirect_uri: redirectUri,
            state: typeof state === "string" ? state : JSON.stringify(state),
            code_challenge: codeChallenge,
            code_challenge_method: codeChallengeMethod,
            device_id: this.deviceId
        });

        try {
            const response = await fetch(`${this.authApiBaseUrl}/oauth2/auth?${urlParams.toString()}`, {
                method: "GET",
                headers: { Accept: "application/json" },
                credentials: "include"
            });

            if (!response.ok) throw new Error(`HTTP error! status: ${response.status}`);
            return await response.json();
        } catch (error) {
            console.error("Error::", error);
            throw new Error("Something went wrong to start login flow");
        }
    }

    async sendOtp(body) {
        const { flowToken, methodId, identifier } = body;

        try {
            const response = await fetch(`${this.authApiBaseUrl}/oauth2/otp/generate`, {
                method: "POST",
                headers: {
                    "Content-Type": "application/json",
                    "x-device-id": this.deviceId,
                    "x-client-id": this.clientId
                },
                credentials: "include",
                body: JSON.stringify({
                    flow_token: flowToken,
                    method_id: methodId,
                    identifier
                })
            });

            if (!response.ok) {
                const apiError = await this.handleApiError(response);
                throw apiError;
            }

            const data = await response.json();
            this.otpLength = data?.data?.otp_len || DEFAULT_OTP_LENGTH;
            return data;
        } catch (error) {
            console.error("Error sending OTP::", error);

            if (error instanceof BifrostApiError) {
                throw error;
            }

            throw new Error(
                `Failed to send OTP: ${error instanceof Error ? error.message : "Unknown error"}`
            );
        }
    }

    async verifyOtp(body) {
        const { flowToken, methodId, identifier, code } = body;

        try {
            // 1. Call verify otp and get code on /auth/callback?code=
            const requestBody = JSON.stringify({
                flow_token: flowToken,
                method_id: methodId,
                identifier,
                code
            });
            const otpResponse = await fetch(`${this.authApiBaseUrl}/oauth2/otp/verify`, {
                method: "POST",
                headers: {
                    "Content-Type": "application/json",
                    "x-device-id": this.deviceId,
                    "x-client-id": this.clientId
                },
                credentials: "include",
                mode: "cors",
                body: requestBody
            });

            if (!otpResponse.ok) {
                const apiError = await this.handleApiError(otpResponse);
                throw apiError;
            }

            // 1.1 Auth code is simply returned from the CF Worker
            const otpResponseData = await otpResponse.json();

            const authCode = otpResponseData?.code;

            const params = new URLSearchParams({
                code: authCode,
                redirect_uri: this.redirectUri,
                code_verifier: this.codeVerifier,
                client_id: this.clientId,
                device_id: this.deviceId
            });

            // 2. Token endpoint to fetch token details for that client
            const tokenEndpoint = `${this.cfWorkerBaseUrl}/auth/token?${params.toString()}`;
            const tokenResponse = await fetch(tokenEndpoint, {
                method: "GET",
                credentials: "include",
                mode: "cors",
                headers: {
                    "Content-Type": "application/json"
                }
            });

            const tokenResponseData = await tokenResponse.json();

            // 3. Generate client codes from token
            const clientCodesResponse = await fetch(`${this.authApiBaseUrl}/oauth2/client-code`, {
                method: "POST",
                headers: {
                    "Content-Type": "application/json",
                    "x-device-id": this.deviceId
                },
                body: JSON.stringify({
                    token: tokenResponseData?.token
                })
            });

            const clientCodesData = await clientCodesResponse.json();
            const clientCodes =
                clientCodesData && clientCodesData?.data && clientCodesData?.data?.clients_code;

            const urls = Object.entries(clientCodes || {})
                .filter(([, info]) => info !== null && info !== undefined)
                .map(([clientId, info]) => {
                    const { domain, code: clientCode } = info;
                    return `${domain}/auth/exchange?client_id=${clientId}&code=${clientCode}&device_id=${this.deviceId}`;
                });

            if (Array.isArray(urls) && urls.length > 0) {
                void Promise.allSettled(
                    urls.map((url) =>
                        fetch(url, { method: "GET", credentials: "include" }).catch(() => undefined)
                    )
                );
            }

            // Handle WebView: exchange token for code and post message to React Native
            // Wrapped in try-catch to ensure verifyOtp always returns res regardless of WebView handling
            try {
                if (tokenResponseData?.token && this.isWebView()) {
                    await this.handleWebViewTokenExchange(tokenResponseData.token);
                }
            } catch (error) {
                console.error("WebView handling failed:", error);
            }

            return {
                ...tokenResponseData
            };
        } catch (error) {
            console.error("Error verifying OTP::", error);

            if (error instanceof BifrostApiError) {
                throw error;
            }

            // Handle WebView-specific body errors
            if (error instanceof TypeError && error.message.includes("body")) {
                throw new Error(
                    `Failed to verify OTP: Request body error. This may be a WebView compatibility issue. ${error.message}`
                );
            }

            throw new Error(
                `Failed to verify OTP: ${error instanceof Error ? error.message : "Unknown error"}`
            );
        }
    }

    async exchangeTokenForCode({ token }) {
        try {
            const response = await fetch(`${this.authApiBaseUrl}/oauth2/token-exchange`, {
                method: "POST",
                headers: {
                    "Content-Type": "application/json",
                    "x-device-id": this.deviceId
                },
                credentials: "include",
                mode: "cors",
                body: JSON.stringify({
                    exchange_type: "client_to_code",
                    token
                })
            });

            if (!response.ok) {
                const apiError = await this.handleApiError(response);
                throw apiError;
            }

            const data = await response.json();
            return data.data;
        } catch (error) {
            console.error("Error exchanging token for code::", error);
            if (error instanceof BifrostApiError) {
                throw error;
            }
            throw new Error(
                `Failed to exchange token for code: ${error instanceof Error ? error.message : "Unknown error"}`
            );
        }
    }

    async tokenExchange(params) {
        const {
            exchangeType,
            clientId,
            token,
            sessionInfo = {
                platform: "WEB",
                loginMethod: "sso",
                device_info: {
                    device_id: this.deviceId
                }
            }
        } = params;

        try {
            const response = await fetch(`${this.authApiBaseUrl}/oauth2/token-exchange`, {
                method: "POST",
                headers: {
                    "Content-Type": "application/json",
                    "x-device-id": this.deviceId
                },
                credentials: "include",
                mode: "cors",
                body: JSON.stringify({
                    exchange_type: exchangeType,
                    client_id: clientId,
                    token,
                    session_info: sessionInfo
                })
            });

            if (!response.ok) {
                const apiError = await this.handleApiError(response);
                throw apiError;
            }

            return await response.json();
        } catch (error) {
            console.error("Error exchanging token::", error);
            if (error instanceof BifrostApiError) {
                throw error;
            }
            throw new Error(
                `Failed to exchange token: ${error instanceof Error ? error.message : "Unknown error"}`
            );
        }
    }

    async handleApiError(response) {
        try {
            const errorData = await response.json();
            return new BifrostApiError(errorData, response.status);
        } catch {
            const errorText = await response.text();
            const genericError = {
                error_code: `HTTP_${response.status}`,
                error: errorText || `HTTP error: ${response.status}`
            };
            return new BifrostApiError(genericError, response.status);
        }
    }

    getTokenExchangeErrorMessage(errorCode) {
        const errorMessages = {
            AS_FB_01: "Device is blocked. Please contact support or try a different device.",
            AS_FB_02: "Session is blocked. Please start a new login session.",
            AS_FB_04: "Device limit exceeded. Please remove some devices from your account or contact support.",
            AS_FB_05: "User account is blocked. Please contact support for assistance.",
            AS_FB_06: "Device ID mismatch. Please ensure you're using the correct device."
        };

        return errorMessages[errorCode] || `Authentication error: ${errorCode}`;
    }

    async refreshToken(params) {
        const { refreshToken, sessionId, clientId = this.clientId } = params;

        try {
            const response = await fetch(`${this.authApiBaseUrl}/oauth2/token`, {
                method: "POST",
                headers: {
                    "Content-Type": "application/json",
                    "x-device-id": this.deviceId
                },
                credentials: "include",
                mode: "cors",
                body: JSON.stringify({
                    grant_type: "refresh_token",
                    refresh_token: refreshToken,
                    client_id: clientId,
                    session_id: sessionId,
                    redirect_uri: this.redirectUri
                })
            });

            if (!response.ok) {
                const apiError = await this.handleApiError(response);
                throw apiError;
            }

            return await response.json();
        } catch (error) {
            console.error("Error refreshing token::", error);

            if (error instanceof BifrostApiError) {
                throw error;
            }

            throw new Error(
                `Failed to refresh token: ${error instanceof Error ? error.message : "Unknown error"}`
            );
        }
    }

    async introspectToken(params) {
        const { token } = params;

        try {
            const response = await fetch(`${this.authApiBaseUrl}/oauth2/introspect`, {
                method: "POST",
                headers: {
                    "Content-Type": "application/json",
                    "x-device-id": this.deviceId
                },
                credentials: "include",
                mode: "cors",
                body: JSON.stringify({ token })
            });

            if (!response.ok) {
                const apiError = await this.handleApiError(response);
                throw apiError;
            }

            return await response.json();
        } catch (error) {
            console.error("Error introspecting token::", error);

            if (error instanceof BifrostApiError) {
                throw error;
            }

            throw new Error(
                `Failed to introspect token: ${error instanceof Error ? error.message : "Unknown error"}`
            );
        }
    }

    async logout(params) {
        const { sessionId, accessToken, deviceIds = [] } = params;

        const logoutBody = {};

        if (sessionId) {
            logoutBody.session_id = sessionId;
        } else if (deviceIds.length > 0) {
            logoutBody.device_ids = deviceIds;
        }

        try {
            const response = await fetch(`${this.cfWorkerBaseUrl}/auth/logout`, {
                method: "POST",
                headers: {
                    "Content-Type": "application/json",
                    "x-device-id": this.deviceId
                },
                credentials: "include",
                mode: "cors",
                body: JSON.stringify({
                    access_token: accessToken,
                    ...logoutBody
                })
            });

            if (!response.ok) {
                const apiError = await this.handleApiError(response);
                throw apiError;
            }

            return await response.json();
        } catch (error) {
            console.error("Error during logout::", error);

            if (error instanceof BifrostApiError) {
                throw error;
            }

            throw new Error(
                `Failed to logout: ${error instanceof Error ? error.message : "Unknown error"}`
            );
        }
    }

    async checkAuth(accessToken) {
        try {
            const response = await fetch(`${this.authApiBaseUrl}/oauth2/introspect`, {
                method: "GET",
                headers: {
                    "Content-Type": "application/json",
                    Authorization: `Bearer ${accessToken}`
                }
            });

            if (!response.ok) return { status: false };

            const responseData = await response.json();
            const isActive = responseData.data.active;
            const expirationTime = responseData.data.exp;
            const currentTimestamp = Math.floor(Date.now() / MILLISECONDS_TO_SECONDS);
            const secondsUntilExpiration = expirationTime - currentTimestamp;
            const redirectUri = this.redirectUri;
            const nearExpirationThreshold = NEAR_EXPIRATION_THRESHOLD;

            if (
                !isActive ||
                secondsUntilExpiration <= 0 ||
                secondsUntilExpiration <= nearExpirationThreshold
            ) {
                const params = new URLSearchParams({ deviceId: this.deviceId });
                if (redirectUri) params.set("redirectUri", redirectUri);

                const res = await fetch(`/auth/refresh?${params.toString()}`, {
                    method: "GET",
                    credentials: "include",
                    headers: { "Content-Type": "application/json" }
                });
                if (res.ok) {
                    const tokenRes = await res.json();
                    return { token: tokenRes?.token, status: true };
                }
            }

            return { status: true };
        } catch (error) {
            console.error("Error checking auth::", error);
            return { status: false };
        }
    }

    async modifyAuth(params) {
        const { access_token } = params;
        try {
            const data = await this.tokenExchange({
                exchangeType: "pvt_to_client",
                clientId: this.clientId,
                token: access_token,
                sessionInfo: {
                    platform: "WEB",
                    loginMethod: "sso",
                    device_info: {
                        device_id: this.deviceId
                    }
                }
            });
            if (data.success) {
                return { access_token: data.data.access_token };
            }
        } catch (error) {
            console.error("Error modifying auth::", error);
            if (error instanceof BifrostApiError) {
                throw error;
            }
            throw new Error(
                `Failed to modify auth: ${error instanceof Error ? error.message : "Unknown error"}`
            );
        }
        throw new Error("Failed to modify auth: unsuccessful token exchange");
    }

    async exchangeCodeForTokens(params) {
        const { code, redirectUri, codeVerifier, sessionInfo } = params;

        try {
            const response = await fetch(`${this.authApiBaseUrl}/oauth2/token`, {
                method: "POST",
                headers: {
                    "Content-Type": "application/json",
                    "x-device-id": this.deviceId
                },
                credentials: "include",
                mode: "cors",
                body: JSON.stringify({
                    grant_type: "authorization_code",
                    code,
                    redirect_uri: redirectUri,
                    client_id: this.clientId,
                    code_verifier: codeVerifier,
                    session_info: sessionInfo
                })
            });

            if (!response.ok) {
                try {
                    // Try to parse the error response
                    const errorData = await response.json();

                    // Handle specific error codes
                    const errorMessage = this.getTokenExchangeErrorMessage(errorData.error_code);
                    throw new Error(`${errorMessage} (Code: ${errorData.error_code})`);
                } catch {
                    // If we can't parse the error response, fall back to generic error
                    const errorText = await response.text();
                    throw new Error(`HTTP error! status: ${response.status}, message: ${errorText}`);
                }
            }

            return await response.json();
        } catch (error) {
            console.error("Error exchanging code for tokens::", error);

            // Re-throw our custom errors with their specific messages
            if (error instanceof Error && error.message.includes("Code:")) {
                throw error;
            }

            // Generic error for other cases
            throw new Error(
                `Failed to exchange code for tokens: ${error instanceof Error ? error.message : "Unknown error"}`
            );
        }
    }

    async sso(params) {
        // Generate PKCE parameters
        const { codeChallenge } = await this.generatePKCES256();

        // Start login flow to get flow_token
        const response = await this.startLoginFlow({
            redirectUri: this.redirectUri,
            codeChallenge,
            state: {
                deviceId: this.deviceId,
                clientId: this.clientId,
                redirectUri: this.redirectUri,
                isSSO: true
            }
        });

        // Extract flow_token from response
        const flowToken = response.data.flow_token;

        // Build state object with required parameters
        const { method_id, country_code, client_id, redirect_uri } = params;
        const stateObject = {
            method_id,
            flow_token: flowToken,
            device_id: this.deviceId,
            country_code,
            client_id: this.clientId,
            redirect_uri: this.redirectUri,
            client_redirect_uri: this.redirectUri
        };
        const stateJson = JSON.stringify(stateObject);

        // Browser-compatible base64 encoding for UTF-8 strings
        const encodedState = btoa(unescape(encodeURIComponent(stateJson)))
            .replace(/\+/g, "-")
            .replace(/\//g, "_")
            .replace(/=/g, "");

        let oauthUrl;

        // Handle different SSO providers
        if (method_id === "GOOGLE_SSO") {
            const scope = encodeURIComponent(
                "https://www.googleapis.com/auth/userinfo.email https://www.googleapis.com/auth/userinfo.profile openid"
            );
            const responseType = "code";
            const prompt = "consent";
            const encodedRedirectUri = encodeURIComponent(redirect_uri);
            oauthUrl = `https://accounts.google.com/o/oauth2/v2/auth?redirect_uri=${encodedRedirectUri}&prompt=${prompt}&response_type=${responseType}&client_id=${client_id}&scope=${scope}&state=${encodedState}`;
        } else {
            throw new Error(`Unsupported SSO method: ${method_id}`);
        }

        // Open OAuth popup
        window.location.href = oauthUrl;

        // Common code for all SSO providers - handles callback and token exchange
        return new Promise((resolve, reject) => {
            window.onmessage = event => {
                if (event.data.type === "BIFROST_SSO_CALLBACK") {
                    const token = event.data.payload?.token;
                    const error = event.data.payload?.error;
                    const errorDescription = event.data.payload?.error_description;

                    // Handle error from OAuth callback
                    if (error) {
                        window.onmessage = null;

                        // Try to extract client_redirect_uri from state and redirect to clean URL
                        try {
                            const stateStr = event.data.payload?.state;
                            let decodedState;

                            if (typeof stateStr === "string") {
                                try {
                                    decodedState = JSON.parse(stateStr);
                                } catch {
                                    // If state is base64 encoded
                                    decodedState = JSON.parse(
                                        decodeURIComponent(escape(atob(stateStr.replace(/-/g, "+").replace(/_/g, "/"))))
                                    );
                                }
                            } else {
                                decodedState = stateStr;
                            }

                            const clientRedirectUri = decodedState?.client_redirect_uri || this.redirectUri;

                            // Log the error for debugging
                            console.error("OAuth authentication error:", {
                                error,
                                errorDescription,
                                errorCode: event.data.payload?.error_code
                            });

                            // Reject the promise first (allows client's catch/finally to run)
                            reject(new Error(errorDescription || error || "Authentication cancelled"));

                            // Then redirect to CLEAN URL after a small delay
                            setTimeout(() => {
                                const cleanUrl = new URL(clientRedirectUri);
                                window.location.href = cleanUrl.pathname;
                            }, 100);
                        } catch (err) {
                            console.error("Failed to parse state or redirect:", err);
                            // Reject promise
                            reject(new Error(errorDescription || error || "Authentication failed"));
                            // Fallback to default redirectUri (clean)
                            setTimeout(() => {
                                const fallbackUrl = new URL(this.redirectUri);
                                window.location.href = fallbackUrl.pathname;
                            }, 100);
                        }
                        return;
                    }

                    if (token) {
                        const responseData = {
                            exchange_urls: event.data.payload?.exchange_urls,
                            expires_in: event.data.payload?.expires_in,
                            session_id: event.data.payload?.session_id,
                            state:
                                typeof event.data.payload?.state === "string"
                                    ? JSON.parse(event.data.payload.state)
                                    : event.data.payload?.state,
                            token,
                            refresh_token: event.data.payload?.refreshToken,
                            message: event.data.payload?.message
                        };

                        if (
                            Array.isArray(responseData.exchange_urls) &&
                            responseData.exchange_urls.length > 0
                        ) {
                            void Promise.allSettled(
                                responseData.exchange_urls.map((url) =>
                                    fetch(url, { method: "GET", credentials: "include" }).catch(() => undefined)
                                )
                            );
                        }

                        window.onmessage = null;

                        resolve(responseData);
                    } else {
                        window.onmessage = null;
                        reject(new Error("No token received from SSO callback"));
                    }
                }
            };
        });
    }

    async getAuthConfig({ configApiBaseUrl }) {
        try {
            const configApiResponse = await fetch(`${configApiBaseUrl}/api/v1/user-login/config`, {
                method: "GET",
                headers: {
                    appId: "com.cars24.seller",
                    vehicleType: "CAR",
                    countryType: "IN"
                }
            });

            const loginMethodsApiResponse = await fetch(
                `${this.authApiBaseUrl}/oauth2/method/${this.clientId}`,
                {
                    method: "GET",
                    headers: { Accept: "application/json" }
                }
            );

            const configData = await configApiResponse.json();
            const loginData = await loginMethodsApiResponse.json();

            return {
                config: configData,
                loginMethods: loginData.data,
                flowToken: undefined
            };
        } catch (error) {
            console.error("Error::", error);
        }
    }

    async getUserLoginConfig({ configApiBaseUrl }) {
        try {
            const response = await fetch(`${configApiBaseUrl}/api/v1/user-login/config`, {
                method: "GET",
                headers: {
                    appId: "com.cars24.seller",
                    vehicleType: "CAR",
                    countryType: "IN"
                }
            });

            return await response.json();
        } catch (error) {
            console.error("Error fetching user-login config::", error);
        }
    }

    async checkSession() {
        try {
            const params = new URLSearchParams({ deviceId: this.deviceId });
            if (this.redirectUri) params.set("redirectUri", this.redirectUri);

            const res = await fetch(`/auth/refresh?${params.toString()}`, {
                method: "GET",
                credentials: "include",
                headers: { "Content-Type": "application/json" }
            });

            if (res.ok) {
                const tokenRes = await res.json();
                return { token: tokenRes?.token, success: true };
            }

            return { success: false, token: "" };
        } catch (error) {
            console.error("Error checking session::", error);
            return { success: false, token: "" };
        }
    }

    async refresh() {
        try {
            const currentOrigin = `${window.location.protocol}//${window.location.host}`;
            const params = new URLSearchParams({
                deviceId: this.deviceId,
                redirectUri: this.redirectUri
            });
            const response = await fetch(`${currentOrigin}/auth/refresh?${params.toString()}`, {
                method: "GET",
                credentials: "include"
            });

            const data = await response.json();
            return data;
        } catch (error) {
            console.warn("Origin logout failed, but continuing with main logout:", error);
        }
    }
}
